import React, { useContext, useEffect, useRef, useState } from 'react';
import {
	MapContainer,
	LayersControl,
	WMSTileLayer,
	useMapEvents,
	Marker,
	Popup,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import proj4 from 'proj4';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Button from '@result/result-ui-components/src/components/Button';
import searchFilters from '../../model/searchFilters';
import MapSearch from './MapSearch';
import markerSanitized from '../../assets/icons/SVG/marker-sanitized.svg';
import markerNew from '../../assets/icons/SVG/marker-new.svg';
import markerConfirmed from '../../assets/icons/SVG/marker-confirmed.svg';
import markerDraft from '../../assets/icons/SVG/marker-draft.svg';
import markerDefault from '../../assets/icons/SVG/marker-default.svg';
import unitString from '../../utils/unitUtil';
import { dateToString } from '@result/result-ui-components/src/utils/DateUtils';
import {useDispatch, useSelector} from 'react-redux';
import { addErrorNotification } from '../../state/notification/notifications';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useNavigate } from 'react-router';
import ApiClient from '../../state/api/ApiClient';
import PromiseBuilder from '../../utils/PromiseUtils';
import { PlotContext } from '../../context/PlotContext';
import {setMapPosition} from "../../state/map/mapCoordinates";

const { Overlay } = LayersControl;

proj4.defs(
	'EPSG:3794',
	'+proj=tmerc +lat_0=0 +lon_0=15 +k=0.9999 +x_0=500000 +y_0=-5000000 +ellps=GRS80 +units=m +no_defs'
);

const convertToEPSG3794 = (latlng) => {
	return proj4('EPSG:4326', 'EPSG:3794', [latlng.lng, latlng.lat]);
};


const MapComponent = ({
	hasSearch = true,
	hasCenter = true,
	zoomControl = true,
	initialZoom = 13,
	isFull = true,
	markers = [],
	centerMarker = false,
	hasPopup = true,
	edit = false,
	redirect = false,
}) => {
	const {latitude: initialLatitude, longitude: initialLongitude} = useSelector((state) => state.mapCoordinates);

	const { t } = useTranslation();
	const searchFiltersTypes = searchFilters();
	const mapRef = useRef();
	const [center, setCenter] = useState([initialLatitude, initialLongitude]);
	const [zoom, setZoom] = useState(initialZoom);
	const [info, setInfo] = useState(null);
	const [position, setPosition] = useState(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [searchType, setSearchType] = useState(searchFiltersTypes[0].key);
	const dispatch = useDispatch();

	const createToast = (title, content) => {
		dispatch(
			addErrorNotification({
				title,
				content,
			})
		);
	};

	const MapEventHandler = () => {
		useMapEvents({
			click: (e) => setPosition(e.latlng),
			moveend: (e) => {
				const zoom = e.target.getZoom();
				const center = e.target.getCenter();
				dispatch(setMapPosition({ latitude: center.lat, longitude: center.lng, zoom: zoom }))
			}
		});
		return null;
	};

	const handlePublicDataSearch = async () => {
		let endpoint = 'https://ipi.eprostor.gov.si/jv-api/search?filter=';
		let url = '';

		switch (searchType) {
			case 'PLOT_NUMBER':
				url = `${endpoint}${searchQuery}&source=JV-PAR`; // ex. 1751 497/4
				break;
			default:
				break;
		}

		if (!url) return;

		try {
			const response = await axios.get(url);

			if (response.status === 200 && response.data?.features.length > 0) {
				const plotInformation = response.data.features[0]['properties'];
				setInfo(plotInformation);
				const [lng, lat] = proj4('EPSG:3794', 'EPSG:4326', [
					plotInformation.E_CEN,
					plotInformation.N_CEN,
				]);
				handleGeneralSearch(lat, lng);
			} else {
				createToast(t('plot_not_found_title'), t('plot_not_found_content'));
			}
		} catch (error) {
			createToast(t('plot_error_title'), t('plot_error_content'));
		}
	};

	const handleGeneralSearch = (lat, lng, zoom = 18) => {
		setCenter([lat, lng]);
		setZoom(zoom);
		mapRef.current.setView([lat, lng], zoom);
		setSearchQuery('');
	};

	useEffect(() => {
		setSearchQuery('');
	}, [searchType]);

	const getCurrentLocation = (e) => {
		if ('geolocation' in navigator) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					handleGeneralSearch(
						position.coords.latitude,
						position.coords.longitude
					);
				},
				() => {
					createToast(
						t('user_geolocation_error_title'),
						t('user_geolocation_error_content')
					);
				}
			);
		} else {
			console.log('Geolocation is not available');
		}
	};

	return (
		<div className={`map ${isFull ? 'is-fullHeight' : !edit ? 'is-lg' : ''}`}>
			{hasSearch && (
				<MapSearch
					searchType={searchType}
					setSearchType={setSearchType}
					searchQuery={searchQuery}
					setSearchQuery={setSearchQuery}
					handleGeneralSearch={handleGeneralSearch}
					handlePublicDataSearch={handlePublicDataSearch}
				/>
			)}
			<MapContainer
				center={center}
				zoom={zoom}
				zoomControl={zoomControl}
				ref={mapRef}>
				<LayersControl position="topright">
					<Overlay name={t('map')} checked>
						<WMSTileLayer
							url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
							attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
						/>
					</Overlay>

					<Overlay name={t('satellite')} checked>
						<WMSTileLayer
							url="https://storitve.eprostor.gov.si/ows-pub-wms/SI.GURS.ZPDZ/wms"
							layers="DOF5"
							transparent={true}
							format="image/jpeg"
							tileSize={256}
						/>
					</Overlay>

					<Overlay name={t('plots')}>
						<WMSTileLayer
							url="https://ipi.eprostor.gov.si/wms-si-gurs-kn/wms"
							layers="SI.GURS.KN:OSNOVNI_PARCELE"
							transparent={true}
							format="image/png"
							styles="nep_kn_parcele"
							tileSize={256}
						/>
					</Overlay>

					<Overlay name={t('plot_numbers')}>
						<WMSTileLayer
							url="https://ipi.eprostor.gov.si/wms-si-gurs-kn/wms"
							layers="SI.GURS.KN:OSNOVNI_PARCELE"
							transparent={true}
							format="image/png"
							styles="nep_kn_parcele_lbl"
							tileSize={256}
						/>
					</Overlay>

					<Overlay name={t('cadastral_municipalities')}>
						<WMSTileLayer
							url="https://storitve.eprostor.gov.si/ows-pub-wms/SI.GURS.ZK/wms"
							layers="KO"
							transparent={true}
							format="image/png"
							tileSize={256}
							styles="zk_ko"
						/>
					</Overlay>

					<Overlay name={t('municipalities')}>
						<WMSTileLayer
							url="https://storitve.eprostor.gov.si/ows-pub-wms/SI.GURS.RPE/wms"
							layers="OB"
							transparent={true}
							format="image/png"
							tileSize={256}
							styles="rpe_ob_lbl"
						/>
					</Overlay>

					<Overlay name={t('actual_land_use')}>
						<WMSTileLayer
							url="https://ipi.eprostor.gov.si/wms-si-gurs-kn/wms"
							layers="SI.GURS.KN:DEJANSKE_RABE"
							transparent={true}
							format="image/png"
							tileSize={256}
						/>
					</Overlay>

					<Overlay name={t('dedicated_land_use')}>
						<WMSTileLayer
							url="https://ipi.eprostor.gov.si/wms-si-gurs-kn/wms"
							layers="SI.GURS.KN:NAMENSKE_RABE"
							transparent={true}
							format="image/png"
							tileSize={256}
						/>
					</Overlay>
				</LayersControl>
				<MapEventHandler />
				<LocationMarkers
					initialMarkers={markers}
					mapRef={mapRef}
					centerMarker={centerMarker}
					hasPopup={hasPopup}
				/>
				{
					<FeatureInfo
						position={position}
						edit={edit}
						redirect={redirect}
						setPosition={setPosition}
					/>
				}
			</MapContainer>
			{hasCenter && (
				<div className="relative">
					<Button
						className="fz fz-pin-location location-button"
						onClick={getCurrentLocation}
					/>
				</div>
			)}
		</div>
	);
};

const markerIcons = {
	SANITIZED: L.icon({
		iconUrl: markerSanitized,
		iconSize: [36, 36],
		iconAnchor: [16, 16],
	}),
	NEW: L.icon({
		iconUrl: markerNew,
		iconSize: [36, 36],
		iconAnchor: [16, 16],
	}),
	CONFIRMED: L.icon({
		iconUrl: markerConfirmed,
		iconSize: [36, 36],
		iconAnchor: [16, 16],
	}),
	DRAFT: L.icon({
		iconUrl: markerDraft,
		iconSize: [36, 36],
		iconAnchor: [16, 16],
	}),
	DEFAULT: L.icon({
		iconUrl: markerDefault,
		iconSize: [41, 50],
		iconAnchor: [20, 50],
		popupAnchor: [0, -60],
	}),
};

const LocationMarkers = ({
	initialMarkers,
	mapRef,
	centerMarker = false,
	hasPopup = true,
}) => {
	const { t } = useTranslation();
	const [markers, setMarkers] = useState([]);
	let navigate = useNavigate();
	const url = `/${t('navigation.href.illegal_waste_dumps_list')}/${t(
		'navigation.href.edit'
	)}/`;
	useEffect(() => {
		if (
			initialMarkers &&
			initialMarkers.length > 0 &&
			initialMarkers[0]['longitude'] !== undefined &&
			initialMarkers[0]['latitude'] !== undefined
		) {
			setMarkers(initialMarkers);

			if (centerMarker) {
				mapRef.current.setView([
					initialMarkers[0].latitude,
					initialMarkers[0].longitude,
				]);
			}
		} else {
			setMarkers([]);
		}
	}, [initialMarkers]);

	return (
		<>
			{markers && markers.length > 0 && (
				<MarkerClusterGroup chunkedLoading>
					{markers.map((marker, index) => (
						<Marker
							key={index}
							position={[marker.latitude, marker.longitude]}
							icon={markerIcons[marker.status || 'DEFAULT']}
							eventHandlers={{
								mouseover: (event) => event.target.openPopup(),
								mouseout: (event) => event.target.closePopup(),
								click: () => (marker.id ? navigate(url + marker.id) : ''),
							}}>
							{hasPopup && (
								<Popup>
									<ul>
										<li>
											<span className="has-text-weight-bold">
												{t('insert_by') + ': '}
											</span>
											{marker?.insertBy}
										</li>
										<li>
											<span className="has-text-weight-bold">
												{t('insert_at') + ': '}
											</span>
											{dateToString(marker?.insertAt, true, true)}
										</li>
										<li>
											<span className="has-text-weight-bold">
												{t('area') + ': '}
											</span>
											{unitString('area', marker?.area)}
										</li>
										<li>
											<span className="has-text-weight-bold">
												{t('volume') + ': '}
											</span>
											{unitString('volume', marker?.volume)}
										</li>
										<li>
											<span className="has-text-weight-bold">
												{t('status') + ': '}
											</span>
											{t('waste_dump_type_options.' + marker?.status)}
										</li>
										<li>
											<span className="has-text-weight-bold">
												{t('comment') + ': '}
											</span>
											{marker?.comment}
										</li>
										{marker?.feedback && (
											<Feedback
												positive={marker.feedback?.positive}
												negative={marker.feedback?.negative}
											/>
										)}
									</ul>
								</Popup>
							)}
						</Marker>
					))}
				</MarkerClusterGroup>
			)}
		</>
	);
};

const Feedback = ({ positive, negative }) => {
	if (!positive && !negative) return <></>;

	return (
		<span className="marker-popup-feedback">
			<span className="marker-popup-feedback-positive has-text-primary">
				<i className="icon fz-check" />
				<span>{positive}</span>
			</span>
			<span className="marker-popup-feedback-negative has-text-danger">
				<i className="icon fz-plus" />
				<span>{negative}</span>
			</span>
		</span>
	);
};

const FeatureInfo = ({ position, edit, redirect, setPosition }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [details, setDetails] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const markerRef = useRef(null);
	const popupRef = useRef(null);
	const { plot, setPlot } = useContext(PlotContext);

	useEffect(() => {
		if (position) {
			fetchDetails();
		}
	}, [position]);

	const fetchDetails = () => {
		setIsLoading(true);
		markerRef?.current?.openPopup();

		const request = async () =>
			ApiClient.get('/api/illegal-dumping-location/web/plot-details', {
				latitude: position.lat,
				longitude: position.lng,
			});

		new PromiseBuilder()
			.then(request)
			.then(async (data) => {
				if (data && data.data) setDetails(data.data);
				setIsLoading(false);
			})
			.then(() => {})
			.catch(async () => {
				setIsLoading(false);
			})
			.run();
	};

	const handleLocationChange = () => {
		setPlot({ ...details, longitude: position.lng, latitude: position.lat });

		markerRef?.current?.closePopup();
		setPosition(null);

		if (redirect) {
			navigate(
				`/${t('navigation.href.illegal_waste_dumps_list')}/${t(
					'navigation.href.create'
				)}`
			);
		}
	};

	if (!position) return <></>;

	return (
		<>
			<Marker
				ref={markerRef}
				position={[position.lat, position.lng]}
				icon={markerIcons['DEFAULT']}
				eventHandlers={{
					mouseover: (e) => e.target.openPopup(),
				}}>
				<Popup ref={popupRef}>
					{isLoading ? (
						<div className="is-flex is-justify-content-center is-align-items-center">
							<div className="loader"></div>
						</div>
					) : (
						<div>
							<div className="is-flex is-justify-content-flex-end">
								<i
									className="icon fz fz-times is-clickable"
									onMouseUp={() => {
										markerRef.current?.closePopup();
										setPosition(null);
									}}
								/>
							</div>
							<ul>
								{position && !details && (
									<>
										<li>
											<span className="has-text-weight-bold">{t('lon')}:</span>
											{position?.lng}
										</li>
										<li>
											<span className="has-text-weight-bold">{t('lat')}:</span>
											{position?.lat}
										</li>
									</>
								)}
								{details && (
									<>
										<li>
											<span className="has-text-weight-bold">
												{t('parcel_number')}:
											</span>{' '}
											{details?.plotNumber || '/'}
										</li>
										<li>
											<span className="has-text-weight-bold">
												{t('municipality')}:
											</span>{' '}
											{details?.municipality?.name || '/'}
										</li>
										<li>
											<span className="has-text-weight-bold">
												{t('cadastral_municipality')}:
											</span>{' '}
											{details?.cadastralMunicipality?.name || '/'}
										</li>
										<li>
											<span className="has-text-weight-bold">
												{t('ownership_type')}:
											</span>{' '}
											{details?.ownershipTypes
												?.map((info) => info.name)
												.join(', ') || '/'}
										</li>
										<li>
											<span className="has-text-weight-bold">
												{t('dedicated_land_use')}:
											</span>{' '}
											{details?.dedicatedLandUses
												?.map((info) => info.name)
												.join(', ') || '/'}
										</li>
										<li>
											<span className="has-text-weight-bold">
												{t('actual_land_use')}:
											</span>
											{details?.actualLandUses
												?.map((info) => info.name)
												.join(', ') || '/'}
										</li>
									</>
								)}
							</ul>
							{!details && <div>{t('no_information_for_plot')}</div>}
							<Button
								label={
									(plot || edit) && details
										? t('use_selected_location')
										: (plot || edit) && !details
										? t('update_location')
										: t('create_new_illegal_waste_dump')
								}
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
									handleLocationChange();
								}}
								className="has-text-weight-bold has-text-primary is-size-7 is-underlined"
							/>
						</div>
					)}
				</Popup>
			</Marker>
		</>
	);
};

export default MapComponent;
